<script setup lang="ts">
  import GICollectionColorField from '@/components/form/GICollectionColorField.vue'
  import GenericDialog from '@/components/dialog/GenericDialog.vue'
  import SpeckleLoginNoticeDialog from '@/components/dialog/SpeckleLoginNoticeDialog.vue'
  import TextField from '@/components/form/TextField.vue'
  import ProgressBar from '@/components/shared/ProgressBar.vue'
  import { useImportWarnings } from '@/composables/useImportWarnings'

  const giDataUploadCollectionStore = useGIDataUploadCollectionStore()

  const speckleManagement = useSpeckleManagement(SpeckleMode.IMPORT_GI_DATA)
  const {
    giDataUploadCollectionName,
    giDataUploadCollectionColor,
    importingGIData,
    importProgress,
    canImportGIData
  } = speckleManagement

  const errorMessage = ref('')

  const { warningGroups } = useImportWarnings(() => importProgress.value.data?.errors ?? {})

  watch(
    () => giDataUploadCollectionStore.importFromSpeckleDialog,
    (open) => {
      if (!open) {
        importProgress.value = { percent: null, message: '', data: {} }
      }
    }
  )

  async function importGIDataFromSpeckle() {
    errorMessage.value = ''
    try {
      const warnings = await speckleManagement.importGIDataFromSpeckle()
      giDataUploadCollectionStore.toggleImportFromSpeckleDialog(false)
      if (Object.keys(warnings).length) {
        giDataUploadCollectionStore.showImportWarnings(warnings)
      }
    } catch (error) {
      errorMessage.value = error instanceof Error ? error.message : 'Import failed'
    }
  }
</script>

<template>
  <GenericDialog
    v-if="speckleManagement.canUseSpeckle.value"
    v-model="giDataUploadCollectionStore.importFromSpeckleDialog"
    :max-width="600"
    @update:model-value="giDataUploadCollectionStore.toggleImportFromSpeckleDialog"
  >
    <template #title> Receive GI Data from Speckle </template>
    <template #default>
      <TextField
        v-model="giDataUploadCollectionName"
        label="Name of GI Data Collection"
        :rules="[(v: string) => !!v || 'Name is required']"
        :disabled="importingGIData"
      ></TextField>
      <SpeckleProjectSearch
        :initialization-function="() => {}"
        :disabled="importingGIData"
        class="mt-2"
      ></SpeckleProjectSearch>
      <SpeckleModelSelect
        :disabled="importingGIData"
        variant="filled"
        class="mt-2"
      ></SpeckleModelSelect>
      <SpeckleVersionSelectField
        :disabled="importingGIData"
        class="mt-2"
      ></SpeckleVersionSelectField>
      <GICollectionColorField v-model="giDataUploadCollectionColor" :disabled="importingGIData" />
      <ProgressBar
        v-if="importingGIData"
        class="mt-4"
        :percent="importProgress.percent ?? undefined"
        :indeterminate="importProgress.percent === null"
        :message="importProgress.message"
      />
      <v-alert v-if="warningGroups.length > 0" type="warning" variant="tonal" class="mt-4">
        <p class="mb-1">Some locations were skipped during import:</p>
        <div v-for="group in warningGroups" :key="group.code" class="mt-3">
          <p class="mb-1">{{ group.summary }}</p>
          <p class="ml-6 mb-0" style="max-height: 30vh; overflow-y: auto">
            {{ group.detail }}
          </p>
        </div>
      </v-alert>
      <v-alert v-if="errorMessage" type="error" variant="flat" class="mt-4">
        {{ errorMessage }}
      </v-alert>
    </template>
    <template #actions>
      <v-btn
        variant="text"
        color="primary"
        @click="giDataUploadCollectionStore.toggleImportFromSpeckleDialog(false)"
      >
        Cancel
      </v-btn>
      <v-btn
        variant="text"
        color="primary"
        :disabled="!canImportGIData"
        :loading="importingGIData"
        @click="importGIDataFromSpeckle"
      >
        Receive
      </v-btn>
    </template>
  </GenericDialog>
  <SpeckleLoginNoticeDialog
    v-else
    v-model="giDataUploadCollectionStore.importFromSpeckleDialog"
  ></SpeckleLoginNoticeDialog>
</template>
